import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"
import {
  type AuthUser,
  type LoginPayload,
  type RegisterPayload,
  type LoginResponse,
  getStoredUser,
  setStoredUser,
  setTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  girisSonrasiYol,
} from "@/lib/auth"
import { resetPanelLayouts } from "@/lib/panel-storage"
import { cevrimdisiOnbellegiSil } from "@/lib/cevrimdisi/onbellek"

interface AuthContextValue {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (payload: LoginPayload) => Promise<void>
  register: (payload: RegisterPayload) => Promise<void>
  logout: () => Promise<void>
  /** Zorunlu şifre değişikliği yapıldı: kayıtlı kullanıcıdan işaret kalkar. */
  sifreDegisti: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const qc = useQueryClient()

  // Check existing session on mount
  useEffect(() => {
    const token = getAccessToken()
    if (token && !user) {
      const stored = getStoredUser()
      if (stored) setUser(stored)
    }
    setIsLoading(false)
  }, [])

  const login = useCallback(async (payload: LoginPayload) => {
    const { data } = await api.post<LoginResponse>("/auth/login", payload)
    setTokens(data.tokens)
    setStoredUser(data.user)
    setUser(data.user)
    navigate(girisSonrasiYol(data.user))
  }, [navigate])

  const sifreDegisti = useCallback(() => {
    setUser((u) => {
      if (!u) return u
      const yeni = { ...u, mustChangePassword: false }
      setStoredUser(yeni)
      return yeni
    })
  }, [])

  const register = useCallback(async (payload: RegisterPayload) => {
    const { data } = await api.post<LoginResponse>("/auth/register", payload)
    setTokens(data.tokens)
    setStoredUser(data.user)
    setUser(data.user)
    navigate("/")
  }, [navigate])

  const logout = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken()
      if (refreshToken) {
        await api.post("/auth/logout", { refreshToken })
      }
    } catch {
      // Logout API failure shouldn't block client-side cleanup
    } finally {
      clearTokens()
      // Panel yerleşimleri kullanıcıya ait: bellekte kalırsa aynı cihazda
      // oturum açan sonraki kişi öncekinin düzenini görür ve üstüne yazar.
      resetPanelLayouts()
      /* Çevrimdışı önbellek de kullanıcıya ait: cihazda kalırsa sonraki kişi
         öncekinin dosyalarını görür. (Gönderilmemiş yazma kuyruğu silinmez —
         sahada doldurulan veri kaybolmasın; aynı kullanıcı girince gider.) */
      qc.clear()
      void cevrimdisiOnbellegiSil()
      setUser(null)
      navigate("/giris")
    }
  }, [navigate, qc])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        sifreDegisti,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
